<template>
  <section 
    id="social-links-section" 
    class="social-links-section"
    :aria-label="$t('socialLinks.sectionLabel')"
  >
    <div class="social-links-section__container">
      <!-- Section Header -->
      <div class="social-links-section__header">
        <h2 class="social-links-section__title">
          {{ content.socialLinksTitle || $t('socialLinks.defaultTitle') }}
        </h2>
        <p 
          class="social-links-section__subtitle"
          v-if="content.socialLinksSubtitle"
        >
          {{ content.socialLinksSubtitle }}
        </p>
      </div>

      <!-- Social Links Grid -->
      <div class="social-links-section__grid">
        <a
          v-for="(link, index) in displayLinks"
          :key="`social-${index}`"
          :href="link.url"
          :title="link.title || `${$t('socialLinks.followOn')} ${link.platform}`"
          :aria-label="`${$t('socialLinks.followOn')} ${link.platform}`"
          class="social-link"
          :class="`social-link--${link.platform.toLowerCase()}`"
          target="_blank"
          rel="noopener noreferrer"
          :style="{ '--animation-delay': `${index * 0.1}s` }"
        >
          <!-- Platform Icon -->
          <div class="social-link__icon">
            <!-- Steam -->
            <svg v-if="link.platform.toLowerCase() === 'steam'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.029 4.524 4.524s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.624 0 11.979-5.354 11.979-11.979C23.958 5.354 18.603.001 11.979.001zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.456-.397.957-1.497 1.41-2.454 1.01z"/>
            </svg>
            
            <!-- Instagram -->
            <svg v-else-if="link.platform.toLowerCase() === 'instagram'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            
            <!-- Twitter/X -->
            <svg v-else-if="link.platform.toLowerCase() === 'twitter' || link.platform.toLowerCase() === 'x'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            
            <!-- YouTube -->
            <svg v-else-if="link.platform.toLowerCase() === 'youtube'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            
            <!-- Discord -->
            <svg v-else-if="link.platform.toLowerCase() === 'discord'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9460 2.4189-2.1568 2.4189Z"/>
            </svg>
            
            <!-- TikTok -->
            <svg v-else-if="link.platform.toLowerCase() === 'tiktok'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
            
            <!-- Facebook -->
            <svg v-else-if="link.platform.toLowerCase() === 'facebook'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            
            <!-- Twitch -->
            <svg v-else-if="link.platform.toLowerCase() === 'twitch'" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
            </svg>
            
            <!-- Generic fallback -->
            <svg v-else viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.364 5.636L16.95 7.05A7 7 0 1 0 19 12h2a9 9 0 1 1-2.636-6.364z"/>
            </svg>
          </div>

          <!-- Platform Name -->
          <div class="social-link__content">
            <span class="social-link__platform">{{ link.platform }}</span>
            <span class="social-link__handle" v-if="link.handle">{{ link.handle }}</span>
          </div>

          <!-- External Link Icon -->
          <div class="social-link__external">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 17L17 7M17 7H7M17 7V17"/>
            </svg>
          </div>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface SocialLink {
  platform: string
  url: string
  handle?: string
  title?: string
}

interface SocialLinksContent {
  socialLinksTitle?: string
  socialLinksSubtitle?: string
  socialLinks?: SocialLink[]
}

// Props
interface Props {
  content: SocialLinksContent
}

const props = withDefaults(defineProps<Props>(), {
  content: () => ({})
})

// Computed properties
const displayLinks = computed<SocialLink[]>(() => {
  // Return only the social links from Umbraco, no hardcoded fallbacks
  return props.content.socialLinks || []
})
</script>

<style lang="scss" scoped>
.social-links-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #2c1810 0%, #1a0f0a 100%);
  color: white;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%);
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  &__container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  &__header {
    text-align: center;
    margin-bottom: 60px;

    @media (max-width: 768px) {
      margin-bottom: 40px;
    }
  }

  &__title {
    font-size: 2.5rem;
    font-weight: 800;
    color: white;
    margin-bottom: 24px;
    line-height: 1.1;
    position: relative;
    padding-bottom: 12px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      font-size: 2rem;
      margin-bottom: 20px;
    }
  }

  &__subtitle {
    font-size: 1.25rem;
    color: #cccccc;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media (max-width: 968px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      max-width: 400px;
      margin: 0 auto;
    }

    @media (max-width: 480px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
    }
  }
}

// Social Link Cards - CodePen style
.social-link {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: transparent;
  border: none;
  border-radius: 16px;
  text-decoration: none;
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
  animation: fadeInUp 0.6s ease-out;
  animation-delay: var(--animation-delay);
  animation-fill-mode: both;

  &:hover {
    transform: translateY(-4px);

    .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(255, 255, 255, 0.2);
    }

    .social-link__external {
      opacity: 1;
      transform: translateX(0);
    }
  }

  // Ensure hover works on mobile with active/focus states
  &:active,
  &:focus {
    .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(255, 255, 255, 0.2);
    }
  }

  @media (max-width: 768px) {
    padding: 10px;
    gap: 0;
    justify-content: center;

    // Ensure hover effect works on touch devices
    &:hover,
    &:active,
    &:focus {
      .social-link__icon {
        box-shadow: 0px 0px 0px 8px rgba(255, 255, 255, 0.2);
      }
    }
  }

  // Platform-specific colors
  &--steam {
    .social-link__icon {
      background: #1b2838;
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(27, 40, 56, 0.5);
    }
  }

  &--instagram {
    .social-link__icon {
      background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%);
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(225, 48, 108, 0.5);
    }
  }

  &--x,
  &--twitter {
    .social-link__icon {
      background: #000000;
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(0, 0, 0, 0.5);
    }
  }

  &--youtube {
    .social-link__icon {
      background: #ff0000;
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(255, 0, 0, 0.5);
    }
  }

  &--discord {
    .social-link__icon {
      background: #5865f2;
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(88, 101, 242, 0.5);
    }
  }

  &--tiktok {
    .social-link__icon {
      background: #000000;
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(254, 44, 85, 0.5);
    }
  }

  &--facebook {
    .social-link__icon {
      background: #1877f2;
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(24, 119, 242, 0.5);
    }
  }

  &--twitch {
    .social-link__icon {
      background: #9146ff;
    }
    &:hover .social-link__icon,
    &:active .social-link__icon,
    &:focus .social-link__icon {
      box-shadow: 0px 0px 0px 8px rgba(145, 70, 255, 0.5);
    }
  }

  &__icon {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
    color: white;

    svg {
      width: 30px;
      height: 30px;
      fill: white;
    }

    @media (max-width: 768px) {
      width: 50px;
      height: 50px;
      background: transparent;

      svg {
        width: 25px;
        height: 25px;
      }
    }

    @media (max-width: 480px) {
      width: 45px;
      height: 45px;

      svg {
        width: 22px;
        height: 22px;
      }
    }
  }

  &__content {
    flex: 1;
    min-width: 0;

    @media (max-width: 768px) {
      display: none;
    }
  }

  &__platform {
    display: block;
    font-size: 1.125rem;
    font-weight: 600;
    color: black;
    margin-bottom: 4px;
  }

  &__handle {
    display: block;
    font-size: 0.9375rem;
    color: #cccccc;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__external {
    width: 20px;
    height: 20px;
    opacity: 0;
    transform: translateX(-8px);
    transition: all 0.3s ease;
    flex-shrink: 0;

    svg {
      width: 100%;
      height: 100%;
      stroke-width: 2;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }
}

// Animations
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
